/**
 * Character Creation React Query hooks
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDraftComment,
  addToRoster,
  canCreateCharacter,
  createDraft,
  getCGExplanations,
  getConsequencePoolCatalog,
  createGift,
  createTechnique,
  deleteDraft,
  deleteTechnique,
  getAffinities,
  getBeginnings,
  getBuilds,
  getCGGifts,
  getCGPointBudget,
  getCGTechniqueOptions,
  getDraft,
  getDraftApplication,
  getDraftCGPoints,
  getEffectTypes,
  getFacets,
  getFacetTree,
  getFamilies,
  getFamiliesWithOpenPositions,
  getClaimableTitles,
  getFamilySlots,
  getHouseClaim,
  getFormOptions,
  getGenders,
  getGift,
  getGifts,
  getGlimpseTags,
  getHeightBands,
  getOriginTemplates,
  getPaths,
  getPathSkillSuggestions,
  getResonanceAssociations,
  getResonances,
  getRestrictions,
  getSkillPointBudget,
  getSkillsWithSpecializations,
  getSpecies,
  getStartingAreas,
  getStatDefinitions,
  getNamingRitualConfig,
  getTarotCards,
  getTechniqueStyles,
  getTraditions,
  getWorshippedBeings,
  resubmitDraft,
  selectTradition,
  submitDraftForReview,
  unsubmitDraft,
  updateDraft,
  updateTechnique,
  withdrawDraft,
} from './api';
import type { CharacterDraft, CharacterDraftUpdate } from './types';

export const characterCreationKeys = {
  all: ['character-creation'] as const,
  explanations: () => [...characterCreationKeys.all, 'explanations'] as const,
  startingAreas: () => [...characterCreationKeys.all, 'starting-areas'] as const,
  beginnings: (areaId: number) => [...characterCreationKeys.all, 'beginnings', areaId] as const,
  genders: () => [...characterCreationKeys.all, 'genders'] as const,
  worshippedBeings: () => [...characterCreationKeys.all, 'worshipped-beings'] as const,
  species: () => [...characterCreationKeys.all, 'species'] as const,
  paths: () => [...characterCreationKeys.all, 'paths'] as const,
  cgBudget: () => [...characterCreationKeys.all, 'cg-budget'] as const,
  draftCGPoints: (draftId: number) =>
    [...characterCreationKeys.all, 'draft-cg-points', draftId] as const,
  families: (areaId: number) => [...characterCreationKeys.all, 'families', areaId] as const,
  familiesWithOpenPositions: (areaId?: number) =>
    [...characterCreationKeys.all, 'families-open', areaId] as const,
  draft: () => [...characterCreationKeys.all, 'draft'] as const,
  canCreate: () => [...characterCreationKeys.all, 'can-create'] as const,
  heightBands: () => [...characterCreationKeys.all, 'height-bands'] as const,
  builds: () => [...characterCreationKeys.all, 'builds'] as const,
  formOptions: (speciesId: number, draftId?: number) =>
    [...characterCreationKeys.all, 'form-options', speciesId, draftId ?? null] as const,
  statDefinitions: () => [...characterCreationKeys.all, 'stat-definitions'] as const,
  // Magic system keys
  affinities: () => [...characterCreationKeys.all, 'affinities'] as const,
  resonances: () => [...characterCreationKeys.all, 'resonances'] as const,
  gifts: () => [...characterCreationKeys.all, 'gifts'] as const,
  gift: (giftId: number) => [...characterCreationKeys.all, 'gift', giftId] as const,
  // CG gift/technique options (GiftStage funnel, #2426 Task 10)
  cgGifts: (draftId: number) => [...characterCreationKeys.all, 'cg-gifts', draftId] as const,
  cgTechniqueOptions: (draftId: number, giftId: number) =>
    [...characterCreationKeys.all, 'cg-technique-options', draftId, giftId] as const,
  // Glimpse tag catalog (guided Glimpse flow, #2427)
  glimpseTags: (pathId?: number) =>
    [...characterCreationKeys.all, 'glimpse-tags', pathId ?? null] as const,
  // Origin template catalog (guided origin-story flow, #2478)
  originTemplates: (beginningId: number) =>
    [...characterCreationKeys.all, 'origin-templates', beginningId] as const,
  // Build-your-own magic system keys
  techniqueStyles: () => [...characterCreationKeys.all, 'technique-styles'] as const,
  effectTypes: () => [...characterCreationKeys.all, 'effect-types'] as const,
  restrictions: (effectTypeId?: number) =>
    [...characterCreationKeys.all, 'restrictions', effectTypeId] as const,
  resonanceAssociations: (category?: string) =>
    [...characterCreationKeys.all, 'resonance-associations', category] as const,
  techniques: (giftId?: number) => [...characterCreationKeys.all, 'techniques', giftId] as const,
  consequencePoolCatalog: () => [...characterCreationKeys.all, 'consequence-pool-catalog'] as const,
  // Skills system keys
  skills: () => [...characterCreationKeys.all, 'skills'] as const,
  skillBudget: () => [...characterCreationKeys.all, 'skill-budget'] as const,
  pathSkillSuggestions: (pathId: number) =>
    [...characterCreationKeys.all, 'path-skill-suggestions', pathId] as const,
  // Facet keys
  facets: () => [...characterCreationKeys.all, 'facets'] as const,
  facetTree: () => [...characterCreationKeys.all, 'facet-tree'] as const,
  // Tradition keys
  traditions: (beginningId: number) =>
    [...characterCreationKeys.all, 'traditions', beginningId] as const,
  // Tarot cards key
  tarotCards: () => [...characterCreationKeys.all, 'tarot-cards'] as const,
  namingRitualConfig: () => [...characterCreationKeys.all, 'naming-ritual-config'] as const,
  // Application key
  application: (draftId: number) => [...characterCreationKeys.all, 'application', draftId] as const,
};

export function useCGExplanations() {
  return useQuery({
    queryKey: characterCreationKeys.explanations(),
    queryFn: getCGExplanations,
    staleTime: 5 * 60 * 1000, // 5 minutes — rarely changes
  });
}

export function useStartingAreas() {
  return useQuery({
    queryKey: characterCreationKeys.startingAreas(),
    queryFn: getStartingAreas,
  });
}

export function useBeginnings(areaId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.beginnings(areaId!),
    queryFn: () => getBeginnings(areaId!),
    enabled: !!areaId,
  });
}

export function useGenders() {
  return useQuery({
    queryKey: characterCreationKeys.genders(),
    queryFn: getGenders,
  });
}

export function useWorshippedBeings() {
  return useQuery({
    queryKey: characterCreationKeys.worshippedBeings(),
    queryFn: getWorshippedBeings,
  });
}

export function useSpecies() {
  return useQuery({
    queryKey: characterCreationKeys.species(),
    queryFn: getSpecies,
  });
}

export function usePaths() {
  return useQuery({
    queryKey: characterCreationKeys.paths(),
    queryFn: getPaths,
  });
}

export function useFamilies(areaId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.families(areaId!),
    queryFn: () => getFamilies(areaId!),
    enabled: !!areaId,
  });
}

export function useDraft(enabled = true) {
  return useQuery({
    queryKey: characterCreationKeys.draft(),
    queryFn: getDraft,
    enabled,
  });
}

export function useCanCreateCharacter() {
  return useQuery({
    queryKey: characterCreationKeys.canCreate(),
    queryFn: canCreateCharacter,
  });
}

export function useCreateDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDraft,
    onSuccess: (data) => {
      queryClient.setQueryData(characterCreationKeys.draft(), data);
    },
  });
}

/** Shared mutation key so concurrent draft PATCHes can detect each other. */
const DRAFT_UPDATE_MUTATION_KEY = ['character-creation', 'update-draft'] as const;

export function useUpdateDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: DRAFT_UPDATE_MUTATION_KEY,
    mutationFn: ({ draftId, data }: { draftId: number; data: CharacterDraftUpdate }) =>
      updateDraft(draftId, data),
    // Optimistic merge (2026-07 audit): stat +/- clicks and slider drags used
    // to read the pre-response cache, so rapid inputs computed from stale
    // values and lost increments; the UI also rubber-banded when responses
    // landed out of order.
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries({ queryKey: characterCreationKeys.draft() });
      const previous = queryClient.getQueryData<CharacterDraft>(characterCreationKeys.draft());
      if (previous) {
        queryClient.setQueryData<CharacterDraft>(characterCreationKeys.draft(), {
          ...previous,
          ...data,
          draft_data: { ...previous.draft_data, ...(data.draft_data ?? {}) },
        } as CharacterDraft);
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(characterCreationKeys.draft(), context.previous);
      }
    },
    onSuccess: (data) => {
      // Apply the server echo only when this is the LAST in-flight draft
      // mutation — an out-of-order response otherwise snaps newer optimistic
      // values back to older server state.
      if (queryClient.isMutating({ mutationKey: DRAFT_UPDATE_MUTATION_KEY }) <= 1) {
        queryClient.setQueryData(characterCreationKeys.draft(), data);
      }
      // Spends recorded in draft_data (species/beginnings/etc.) move the CG
      // point budget — keep the Review page's numbers honest.
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.draftCGPoints(data.id) });
    },
  });
}

export function useDeleteDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) => deleteDraft(draftId),
    onSuccess: () => {
      queryClient.setQueryData(characterCreationKeys.draft(), null);
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.canCreate() });
    },
  });
}

export function useSubmitDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, submissionNotes }: { draftId: number; submissionNotes: string }) =>
      submitDraftForReview(draftId, submissionNotes),
    onSuccess: (_data, { draftId }) => {
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.draft() });
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.application(draftId) });
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.canCreate() });
    },
  });
}

export function useAddToRoster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) => addToRoster(draftId),
    onSuccess: () => {
      queryClient.setQueryData(characterCreationKeys.draft(), null);
    },
  });
}

// CG Points hooks
export function useCGPointBudget() {
  return useQuery({
    queryKey: characterCreationKeys.cgBudget(),
    queryFn: getCGPointBudget,
  });
}

export function useDraftCGPoints(draftId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.draftCGPoints(draftId!),
    queryFn: () => getDraftCGPoints(draftId!),
    enabled: !!draftId,
  });
}

// NEW: Family Tree hooks
export function useFamiliesWithOpenPositions(areaId?: number) {
  return useQuery({
    queryKey: characterCreationKeys.familiesWithOpenPositions(areaId),
    queryFn: () => getFamiliesWithOpenPositions(areaId),
  });
}

export function useFamilySlots(familyId: number | undefined) {
  return useQuery({
    queryKey: ['family-slots', familyId],
    queryFn: () => getFamilySlots(familyId!),
    enabled: familyId !== undefined,
  });
}

// NEW: Height Bands and Builds hooks for Appearance stage
export function useHeightBands() {
  return useQuery({
    queryKey: characterCreationKeys.heightBands(),
    queryFn: getHeightBands,
  });
}

export function useBuilds() {
  return useQuery({
    queryKey: characterCreationKeys.builds(),
    queryFn: getBuilds,
  });
}

export function useFormOptions(speciesId: number | undefined, draftId?: number) {
  return useQuery({
    queryKey: characterCreationKeys.formOptions(speciesId!, draftId),
    queryFn: () => getFormOptions(speciesId!, draftId),
    enabled: !!speciesId,
  });
}

// Stat definitions hook for Attributes stage
export function useStatDefinitions() {
  return useQuery({
    queryKey: characterCreationKeys.statDefinitions(),
    queryFn: getStatDefinitions,
  });
}

// =============================================================================
// Magic System hooks
// =============================================================================

export function useAffinities() {
  return useQuery({
    queryKey: characterCreationKeys.affinities(),
    queryFn: getAffinities,
  });
}

export function useResonances() {
  return useQuery({
    queryKey: characterCreationKeys.resonances(),
    queryFn: getResonances,
  });
}

export function useGifts() {
  return useQuery({
    queryKey: characterCreationKeys.gifts(),
    queryFn: getGifts,
  });
}

export function useGift(giftId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.gift(giftId!),
    queryFn: () => getGift(giftId!),
    enabled: !!giftId,
  });
}

/**
 * Gifts pickable for a draft's chosen tradition + path (GiftStage funnel, #2426).
 * Empty until the draft has both a selected tradition and a selected path.
 */
export function useCGGifts(draftId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.cgGifts(draftId!),
    queryFn: () => getCGGifts(draftId!),
    enabled: !!draftId,
  });
}

/**
 * Technique options (pool ∪ signature) for a draft's (path, gift, tradition) pick
 * (GiftStage funnel, #2426).
 */
export function useCGTechniqueOptions(draftId: number | undefined, giftId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.cgTechniqueOptions(draftId!, giftId!),
    queryFn: () => getCGTechniqueOptions(draftId!, giftId!),
    enabled: !!draftId && !!giftId,
  });
}

/**
 * Glimpse tag catalog for the guided Glimpse flow (#2427) — an authored
 * catalog that rarely changes, so a longer staleTime avoids refetching on
 * every CG stage visit.
 */
export function useGlimpseTags(pathId?: number) {
  return useQuery({
    queryKey: characterCreationKeys.glimpseTags(pathId),
    queryFn: () => getGlimpseTags(pathId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useOriginTemplates(beginningId: number | null | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.originTemplates(beginningId ?? 0),
    queryFn: () => getOriginTemplates(beginningId!),
    enabled: !!beginningId,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Build-Your-Own Magic System hooks
// =============================================================================

export function useTechniqueStyles() {
  return useQuery({
    queryKey: characterCreationKeys.techniqueStyles(),
    queryFn: getTechniqueStyles,
  });
}

export function useEffectTypes() {
  return useQuery({
    queryKey: characterCreationKeys.effectTypes(),
    queryFn: getEffectTypes,
  });
}

export function useRestrictions(effectTypeId?: number) {
  return useQuery({
    queryKey: characterCreationKeys.restrictions(effectTypeId),
    queryFn: () => getRestrictions(effectTypeId),
  });
}

export function useResonanceAssociations(category?: string) {
  return useQuery({
    queryKey: characterCreationKeys.resonanceAssociations(category),
    queryFn: () => getResonanceAssociations(category),
  });
}

export function useConsequencePoolCatalog() {
  return useQuery({
    queryKey: characterCreationKeys.consequencePoolCatalog(),
    queryFn: getConsequencePoolCatalog,
  });
}

export function useCreateGift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createGift,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.gifts() });
    },
  });
}

export function useCreateTechnique() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTechnique,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: characterCreationKeys.techniques(data.gift),
      });
    },
  });
}

export function useUpdateTechnique() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      techniqueId,
      data,
    }: {
      techniqueId: number;
      data: Parameters<typeof updateTechnique>[1];
    }) => updateTechnique(techniqueId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: characterCreationKeys.techniques(data.gift),
      });
    },
  });
}

export function useDeleteTechnique() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTechnique,
    onSuccess: () => {
      // Invalidate all technique queries since we don't know the gift
      queryClient.invalidateQueries({
        queryKey: [...characterCreationKeys.all, 'techniques'],
      });
    },
  });
}

// =============================================================================
// Skills System hooks
// =============================================================================

/**
 * Get all skills with their specializations.
 */
export function useSkills() {
  return useQuery({
    queryKey: characterCreationKeys.skills(),
    queryFn: getSkillsWithSpecializations,
  });
}

/**
 * Get skill point budget configuration.
 */
export function useSkillPointBudget() {
  return useQuery({
    queryKey: characterCreationKeys.skillBudget(),
    queryFn: getSkillPointBudget,
  });
}

/**
 * Get skill suggestions for a specific path.
 */
export function usePathSkillSuggestions(pathId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.pathSkillSuggestions(pathId!),
    queryFn: () => getPathSkillSuggestions(pathId!),
    enabled: !!pathId,
  });
}

// =============================================================================
// Facet Hooks
// =============================================================================

export function useFacets() {
  return useQuery({
    queryKey: characterCreationKeys.facets(),
    queryFn: getFacets,
  });
}

export function useFacetTree() {
  return useQuery({
    queryKey: characterCreationKeys.facetTree(),
    queryFn: getFacetTree,
  });
}

// =============================================================================
// Tarot Card Hooks
// =============================================================================

export function useTarotCards() {
  return useQuery({
    queryKey: characterCreationKeys.tarotCards(),
    queryFn: getTarotCards,
  });
}

export function useNamingRitualConfig() {
  return useQuery({
    queryKey: characterCreationKeys.namingRitualConfig(),
    queryFn: getNamingRitualConfig,
  });
}

// =============================================================================
// Traditions Hooks
// =============================================================================

export function useTraditions(beginningId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.traditions(beginningId!),
    queryFn: () => getTraditions(beginningId!),
    enabled: !!beginningId,
  });
}

export function useSelectTradition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, traditionId }: { draftId: number; traditionId: number | null }) =>
      selectTradition(draftId, traditionId),
    onSuccess: (_data, { draftId }) => {
      // Invalidate draft since tradition affects magic validation
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.draft() });
      // Selecting a tradition may auto-add a required distinction, affecting CG points
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.draftCGPoints(draftId) });
      // The gift (and downstream technique) catalog is scoped per-tradition — refetch
      // immediately so GiftSelector's stale-pick reset (and the funnel UI generally)
      // sees the new tradition's options right away instead of on the next unrelated
      // refetch. Invalidate the technique-options key by its draftId-scoped prefix
      // (no giftId yet known here) so every cached (draftId, giftId) entry is covered.
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.cgGifts(draftId) });
      queryClient.invalidateQueries({
        queryKey: [...characterCreationKeys.all, 'cg-technique-options', draftId],
      });
    },
  });
}

// =============================================================================
// Application Hooks
// =============================================================================

export function useDraftApplication(draftId: number | undefined) {
  return useQuery({
    queryKey: characterCreationKeys.application(draftId!),
    queryFn: () => getDraftApplication(draftId!),
    enabled: !!draftId,
  });
}

export function useUnsubmitDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) => unsubmitDraft(draftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.all });
    },
  });
}

export function useResubmitDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, comment }: { draftId: number; comment?: string }) =>
      resubmitDraft(draftId, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.all });
    },
  });
}

export function useWithdrawDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) => withdrawDraft(draftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: characterCreationKeys.all });
    },
  });
}

export function useAddDraftComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, text }: { draftId: number; text: string }) =>
      addDraftComment(draftId, text),
    onSuccess: (_data, { draftId }) => {
      queryClient.invalidateQueries({
        queryKey: characterCreationKeys.application(draftId),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// House creator (#1884 Phase D)
// ---------------------------------------------------------------------------

export function useClaimableTitles() {
  return useQuery({
    queryKey: ['character-creation', 'house-titles'],
    queryFn: getClaimableTitles,
  });
}

export function useHouseClaim(draftId: number | undefined) {
  return useQuery({
    queryKey: ['character-creation', 'house-claim', draftId],
    queryFn: () => getHouseClaim(draftId!),
    enabled: draftId !== undefined,
  });
}
